import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { apiClient, getErrorMessage } from '../../api/client'
import { useOfflineFirstQuery } from '../../hooks/useOfflineFirstQuery'
import { resolveTenantSlug } from '../../lib/tenant'

type PaginatedResponse<T> = {
  count: number
  next: string | null
  previous: string | null
  results: T[]
}

type ClinicalNoteListItem = {
  id: number
  session: number
  session_date: string
  client_name: string
  service_code: string
  status: string
  status_display: string
  created_by_name: string
  signed_by_name: string | null
  signed_at: string | null
  locked_at: string | null
  created_at: string
}

const FILTERS: Array<{ value: string; label: string }> = [
  { value: 'all', label: 'All notes' },
  { value: 'pending', label: 'Pending signature/attestation' },
  { value: 'locked', label: 'Locked' },
  { value: 'draft', label: 'Draft' },
  { value: 'signed', label: 'Signed' },
]

export default function NotesListPage() {
  const tenantSlug = resolveTenantSlug()
  const [searchParams, setSearchParams] = useSearchParams()
  const [filter, setFilter] = useState(searchParams.get('filter') ?? 'all')

  const cacheKey = useMemo(() => {
    return `tenant:${tenantSlug ?? 'unknown'}:notes:list:filter=${filter}`
  }, [filter, tenantSlug])

  const query = useOfflineFirstQuery<PaginatedResponse<ClinicalNoteListItem> | ClinicalNoteListItem[]>({
    queryKey: ['clinical-notes', { filter }],
    cacheKey,
    cacheMaxAgeMs: 5 * 60 * 1000,
    queryFn: async () => {
      if (filter === 'pending') {
        const res = await apiClient.get<ClinicalNoteListItem[]>('/api/v1/clinical-notes/pending/')
        return res.data
      }

      const params: Record<string, string> = {}
      if (filter !== 'all') params.status = filter
      const res = await apiClient.get<PaginatedResponse<ClinicalNoteListItem>>('/api/v1/clinical-notes/', {
        params,
      })
      return res.data
    },
  })

  const rows: ClinicalNoteListItem[] = Array.isArray(query.data)
    ? query.data
    : query.data?.results ?? []

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Clinical Notes</h1>
          <p className="text-sm text-gray-600">Draft, sign, attest, and lock notes. Addendums only after lock.</p>
        </div>
      </div>

      <div className="card p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="label" htmlFor="filter">
              Filter
            </label>
            <select
              id="filter"
              className="input mt-1"
              value={filter}
              onChange={(e) => {
                const v = e.target.value
                setFilter(v)
                const next = new URLSearchParams(searchParams)
                if (v === 'all') next.delete('filter')
                else next.set('filter', v)
                setSearchParams(next, { replace: true })
              }}
            >
              {FILTERS.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {query.isError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {getErrorMessage(query.error)}
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="table-header">Date</th>
                <th className="table-header">Client</th>
                <th className="table-header">Service</th>
                <th className="table-header">Status</th>
                <th className="table-header">Created By</th>
                <th className="table-header">Signed By</th>
                <th className="table-header">Locked</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {query.isLoading && rows.length === 0 ? (
                Array.from({ length: 10 }).map((_, idx) => (
                  <tr key={idx}>
                    <td className="table-cell"><div className="h-4 w-24 bg-gray-200 rounded animate-pulse" /></td>
                    <td className="table-cell"><div className="h-4 w-48 bg-gray-200 rounded animate-pulse" /></td>
                    <td className="table-cell"><div className="h-4 w-16 bg-gray-200 rounded animate-pulse" /></td>
                    <td className="table-cell"><div className="h-4 w-28 bg-gray-200 rounded animate-pulse" /></td>
                    <td className="table-cell"><div className="h-4 w-32 bg-gray-200 rounded animate-pulse" /></td>
                    <td className="table-cell"><div className="h-4 w-32 bg-gray-200 rounded animate-pulse" /></td>
                    <td className="table-cell"><div className="h-4 w-24 bg-gray-200 rounded animate-pulse" /></td>
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="table-cell text-gray-500">
                    No notes found.
                  </td>
                </tr>
              ) : (
                rows.map((n) => (
                  <tr key={n.id} className="hover:bg-gray-50">
                    <td className="table-cell">
                      <Link to={`/app/notes/${n.id}`} className="font-medium text-gray-900 hover:text-primary-600">
                        {n.session_date}
                      </Link>
                    </td>
                    <td className="table-cell">{n.client_name}</td>
                    <td className="table-cell">{n.service_code}</td>
                    <td className="table-cell">{n.status_display ?? n.status}</td>
                    <td className="table-cell">{n.created_by_name}</td>
                    <td className="table-cell">{n.signed_by_name ?? '-'}</td>
                    <td className="table-cell">{n.locked_at ? n.locked_at.slice(0, 10) : '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

